import { Box, Stack, useDisclosure, useToast } from "@chakra-ui/react";
import { type FC, useState } from "react";
import { useParams } from "react-router";

import { DocusealOverlay } from "@equidefi/portals/components/docuseal/DocusealOverlay";
import { BannerAlert, Heading, Paper, Text } from "@equidefi/ui";

import NavBar from "@/components/nav/NavBar";

import { WorkflowContainer } from "../workflow/WorkflowContainer";

const TEST_BANNER = "This is a test agreement — not a real signing request and not legally binding.";

const UnsignedScreen: FC<{ slug: string; token: string; onSigned: () => void }> = ({ slug, token, onSigned }) => {
  const toast = useToast();
  const overlay = useDisclosure();
  const src = `https://docuseal.com/s/${slug}`;

  return (
    <WorkflowContainer
      title="Test Agreement"
      buttonLabel="Continue to Sign"
      buttonProps={{ display: overlay.isOpen ? "none" : "block" }}
      onContinue={overlay.onOpen}>
      <BannerAlert status="warning" description={TEST_BANNER} my={4} />

      <Text mb={4}>
        You've been invited to preview an investment agreement. No agreement will be created and no party will be
        legally bound by your signature.
      </Text>

      <DocusealOverlay
        isOpen={overlay.isOpen}
        onClose={overlay.onClose}
        token={token}
        src={src}
        name="Test Agreement"
        onComplete={() => {
          overlay.onClose();
          toast({ status: "success", description: "Test agreement signed!" });
          onSigned();
        }}
      />
    </WorkflowContainer>
  );
};

const SignedScreen: FC = () => (
  <WorkflowContainer>
    <BannerAlert status="warning" description={TEST_BANNER} my={4} />
    <Box maxWidth="600px" width="100%" mx="auto" mt={6}>
      <Heading as="h1" fontWeight="bold" mb={2}>
        Test Signing Complete
      </Heading>
      <Text mb={4}>You finished previewing this agreement. No legal record was created.</Text>
      <Paper variant="primary" p={4} mb={6}>
        <Heading as="h3" mb={0} textStyle="h2">
          Test Agreement
        </Heading>
        <Text mb={0} fontSize="md" textStyle="context">
          Signed (test only)
        </Text>
      </Paper>
      <Text align="center">You can safely close this page.</Text>
    </Box>
  </WorkflowContainer>
);

export const TestSigner: FC = () => {
  const { slug, token } = useParams<{ slug: string; token: string }>();
  const [signed, setSigned] = useState(false);

  if (!slug || !token) {
    return (
      <Stack as="main" bgColor="background" direction={{ base: "column", lg: "row" }} minHeight="100vh">
        <NavBar position="1">
          <WorkflowContainer title="Invalid Test Link">
            <Text>This preview link is invalid or has expired.</Text>
          </WorkflowContainer>
        </NavBar>
      </Stack>
    );
  }

  return (
    <Stack as="main" bgColor="background" direction={{ base: "column", lg: "row" }} minHeight="100vh">
      <NavBar position={signed ? "8" : "1"}>
        {signed ? <SignedScreen /> : <UnsignedScreen slug={slug} token={token} onSigned={() => setSigned(true)} />}
      </NavBar>
    </Stack>
  );
};
